import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { articleBySlug, ARTICLES } from "@/data/articles";
import AppHeader from "@/components/AppHeader";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

const CATEGORY_COLORS = {
  "Password Recovery": "text-[#FFD700] border-[#FFD700]/40",
  "Wallet Reviews": "text-[#10B981] border-[#10B981]/40",
  DeFi: "text-[#7DD3FC] border-[#7DD3FC]/40",
};

// Custom renderers keep the article's prose consistent with the Swiss dark
// aesthetic used across the rest of the app.
const mdComponents = {
  h1: (p) => (
    <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-12 mb-4">
      {p.children}
    </h1>
  ),
  h2: (p) => (
    <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tighter mt-12 mb-4 text-white">
      {p.children}
    </h2>
  ),
  h3: (p) => (
    <h3 className="font-display text-xl font-bold tracking-tight mt-8 mb-3 text-white">
      {p.children}
    </h3>
  ),
  h4: (p) => (
    <h4 className="text-sm font-mono uppercase tracking-widest text-[#FFD700] mt-6 mb-2">
      {p.children}
    </h4>
  ),
  p: (p) => (
    <p className="text-white/75 leading-relaxed text-[15px] sm:text-base mb-5">
      {p.children}
    </p>
  ),
  ul: (p) => (
    <ul className="space-y-2 mb-5 pl-5 list-disc marker:text-[#FFD700]/60 text-white/75 text-[15px] leading-relaxed">
      {p.children}
    </ul>
  ),
  ol: (p) => (
    <ol className="space-y-2 mb-5 pl-5 list-decimal marker:text-[#FFD700]/60 text-white/75 text-[15px] leading-relaxed">
      {p.children}
    </ol>
  ),
  li: (p) => <li>{p.children}</li>,
  strong: (p) => <strong className="text-white font-semibold">{p.children}</strong>,
  em: (p) => <em className="text-white/90">{p.children}</em>,
  blockquote: (p) => (
    <blockquote className="border-l-2 border-[#FFD700]/60 pl-4 my-5 text-white/70 italic">
      {p.children}
    </blockquote>
  ),
  code: ({ inline, children }) =>
    inline ? (
      <code className="bg-white/5 border border-white/10 px-1.5 py-0.5 font-mono text-[13px] text-[#FFD700]">
        {children}
      </code>
    ) : (
      <pre className="bg-black border border-white/10 p-4 overflow-x-auto font-mono text-[13px] mb-5">
        <code>{children}</code>
      </pre>
    ),
  table: (p) => (
    <div className="overflow-x-auto mb-6 border border-white/10">
      <table className="w-full text-sm font-mono">{p.children}</table>
    </div>
  ),
  thead: (p) => <thead className="bg-white/5">{p.children}</thead>,
  th: (p) => (
    <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-white/60 border-b border-white/10">
      {p.children}
    </th>
  ),
  td: (p) => (
    <td className="px-3 py-2 text-white/75 border-b border-white/5 align-top">
      {p.children}
    </td>
  ),
  a: (p) => <span className="text-white underline underline-offset-2">{p.children}</span>,
  hr: () => <hr className="border-white/10 my-8" />,
};

export default function ArticlePage() {
  const { slug } = useParams();
  const article = articleBySlug(slug);
  const catCls =
    (article && CATEGORY_COLORS[article.category]) ||
    "text-white/60 border-white/20";

  const related = article
    ? ARTICLES.filter(
        (a) => a.category === article.category && a.slug !== article.slug
      ).slice(0, 3)
    : [];

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <AppHeader />
      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Link
            to="/dashboard"
            data-testid="article-back-btn"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/50 hover:text-white transition-colors mb-10"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to knowledge center
          </Link>

          {!article ? (
            <div data-testid="article-not-found" className="py-24 text-center">
              <p className="text-xs font-mono uppercase tracking-widest text-[#F23F42] mb-3">
                404
              </p>
              <h1 className="font-display text-3xl font-black tracking-tighter mb-3">
                Article not found
              </h1>
              <p className="text-white/60 text-sm mb-6">
                The article you're looking for doesn't exist or has been removed.
              </p>
              <Link
                to="/dashboard"
                className="inline-block px-4 py-2 border border-white/20 hover:border-white text-sm font-mono uppercase tracking-widest"
              >
                Return to dashboard
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-8" data-testid={`article-page-${article.slug}`}>
                <span
                  className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${catCls} mb-6`}
                >
                  {article.category}
                </span>
                <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-4 leading-tight">
                  {article.title}
                </h1>
                <p className="text-white/60 text-base sm:text-lg mb-6">
                  {article.excerpt}
                </p>
                <div className="flex items-center gap-4 text-[10px] font-mono uppercase tracking-widest text-white/40 border-t border-white/10 pt-4">
                  <span>{article.date}</span>
                  <span>·</span>
                  <span>{article.readMinutes} min read</span>
                </div>
              </div>

              <article className="prose-inv" data-testid="article-body">
                <ReactMarkdown components={mdComponents}>
                  {article.content}
                </ReactMarkdown>
              </article>

              {related.length > 0 && (
                <div className="mt-16 pt-10 border-t border-white/10">
                  <p className="text-xs font-mono uppercase tracking-widest text-[#FFD700] mb-6">
                    / Keep reading
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {related.map((a) => (
                      <Link
                        key={a.slug}
                        to={`/knowledge/${a.slug}`}
                        data-testid={`article-related-${a.slug}`}
                        className="group bg-[#0A0A0A] border border-white/10 hover:border-[#FFD700]/40 p-5 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span
                            className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${
                              CATEGORY_COLORS[a.category] ||
                              "text-white/60 border-white/20"
                            }`}
                          >
                            {a.category}
                          </span>
                          <ArrowUpRight className="w-4 h-4 text-white/30 group-hover:text-[#FFD700] transition-colors" />
                        </div>
                        <h3 className="font-display text-base font-black tracking-tight leading-snug">
                          {a.title}
                        </h3>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
